import { useState, useEffect } from 'react';

import { supabase } from '../../lib/supabase';
import { useLang } from '../../context/LangContext';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../lib/constants';

const NAV = [
  { id: 'dashboard', icon: '📊', labelAr: 'الرئيسية', labelEn: 'Dashboard' },
  { id: 'businesses', icon: '🏪', labelAr: 'التجار', labelEn: 'Businesses' },
  { id: 'categories', icon: '📂', labelAr: 'المجالات', labelEn: 'Categories' },
  { id: 'ads', icon: '📢', labelAr: 'الإعلانات', labelEn: 'Ads' },
  { id: 'rewards', icon: '🎁', labelAr: 'المكافآت', labelEn: 'Rewards' },
];

export default function AdminApp() {
  const { signOut } = useAuth();
  const { isRtl, toggleLang, lang } = useLang();
  const [screen, setScreen] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ads, setAds] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentBookings, setRecentBookings] = useState([]);
  const [weekChart, setWeekChart] = useState([]);
  const [chartPeriod, setChartPeriod] = useState(7);
  const [chartType, setChartType] = useState('bar');
  const [chartMetric, setChartMetric] = useState('bookings');
  const [allBookings, setAllBookings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [topStats, setTopStats] = useState({ businesses: [], services: [], staff: [] });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const today = new Date().toISOString().split('T')[0];
    const [bizRes, catRes, adsRes, rewRes, bookRes, userRes, recentBookRes, topRes] = await Promise.all([
      supabase.from('businesses').select('*').order('created_at', { ascending: false }),
      supabase.from('categories').select('*').order('sort_order'),
      supabase.from('ads').select('*').order('sort_order'),
      supabase.from('rewards_settings').select('*').single(),
      supabase.from('bookings').select('id, status, price, booking_date'),
      supabase.from('profiles').select('id, role'),
      supabase.from('bookings').select('*, businesses(name_ar, name_en), services(name_ar, name_en), profiles(full_name)').order('created_at', { ascending: false }).limit(5),
      supabase.from('bookings').select('business_id, service_id, staff_id, businesses(name_ar, name_en), services(name_ar, name_en), staff(name_ar, name_en)').neq('status', 'cancelled'),
    ]);
    setBusinesses(bizRes.data || []);
    setCategories(catRes.data || []);
    setAds(adsRes.data || []);
    setRewards(rewRes.data);
    setRecentBookings(recentBookRes.data || []);

    // حساب الأكثر حجزاً
    const topData = topRes.data || [];

    // أكثر مركز
    const bizCount = {};
    topData.forEach(b => {
      if (!b.business_id) return;
      const name = b.businesses ? (b.businesses.name_ar || b.businesses.name_en) : b.business_id;
      bizCount[name] = (bizCount[name] || 0) + 1;
    });
    const topBiz = Object.entries(bizCount).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, count]) => ({ name, count }));

    // أكثر خدمة
    const srvCount = {};
    topData.forEach(b => {
      if (!b.service_id) return;
      const name = b.services ? (b.services.name_ar || b.services.name_en) : b.service_id;
      srvCount[name] = (srvCount[name] || 0) + 1;
    });
    const topSrv = Object.entries(srvCount).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, count]) => ({ name, count }));

    // أكثر خبير
    const stfCount = {};
    topData.forEach(b => {
      if (!b.staff_id) return;
      const name = b.staff ? (b.staff.name_ar || b.staff.name_en) : b.staff_id;
      stfCount[name] = (stfCount[name] || 0) + 1;
    });
    const topStf = Object.entries(stfCount).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, count]) => ({ name, count }));

    setTopStats({ businesses: topBiz, services: topSrv, staff: topStf });

    const allBookings = bookRes.data || [];
    const allUsers = userRes.data || [];
    const completed = allBookings.filter(b => b.status === 'completed');
    const todayBookings = allBookings.filter(b => b.booking_date === today);

    setAllBookings(allBookings);
    setAllUsers(allUsers);

    const weekData = buildChartData(allBookings, allUsers, 7, 'bookings');
    setWeekChart(weekData);

    setStats({
      businesses: bizRes.data?.length || 0,
      activeBusinesses: bizRes.data?.filter(b => b.status === 'active').length || 0,
      pending: bizRes.data?.filter(b => b.status === 'pending').length || 0,
      bookings: allBookings.length,
      todayBookings: todayBookings.length,
      completedBookings: completed.length,
      revenue: completed.reduce((s, b) => s + (b.price || 0), 0),
      customers: allUsers.filter(u => u.role === 'user').length,
      partners: allUsers.filter(u => u.role === 'business').length,
    });
    setLoading(false);
  };

  const buildChartData = (bookings, users, period, metric) => {
    const data = [];
    for (let i = period - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      let count = 0;
      if (metric === 'bookings') {
        count = bookings.filter(b => b.booking_date === dateStr).length;
      } else if (metric === 'revenue') {
        count = bookings.filter(b => b.booking_date === dateStr && b.status === 'completed').reduce((s, b) => s + (b.price || 0), 0);
      }
      const label = period <= 7
        ? d.toLocaleDateString('ar', { weekday: 'short' })
        : period <= 30
          ? d.getDate().toString()
          : d.toLocaleDateString('ar', { month: 'short', day: 'numeric' });
      const labelEn = period <= 7
        ? d.toLocaleDateString('en', { weekday: 'short' })
        : period <= 30
          ? d.getDate().toString()
          : d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
      data.push({ date: dateStr, day: label, dayEn: labelEn, count, isToday: i === 0 });
    }
    return data;
  };

  useEffect(() => {
    if (allBookings.length > 0 || allUsers.length > 0 || chartPeriod) {
      setWeekChart(buildChartData(allBookings, allUsers, chartPeriod, chartMetric));
    }
  }, [chartPeriod, chartMetric, allBookings]);

  const exportReport = () => {
    const reportDate = new Date().toLocaleDateString(isRtl ? 'ar' : 'en', { year: 'numeric', month: 'long', day: 'numeric' });
    const content = `
jadwil - ${isRtl ? 'تقرير الإحصائيات' : 'Statistics Report'}
${reportDate}

═══════════════════════════════
${isRtl ? 'الملخص العام' : 'General Summary'}
═══════════════════════════════
${isRtl ? 'العملاء' : 'Customers'}: ${stats.customers}
${isRtl ? 'الشركاء' : 'Partners'}: ${stats.partners}
${isRtl ? 'الشركاء النشطون' : 'Active Partners'}: ${stats.activeBusinesses}
${isRtl ? 'الشركاء بانتظار الموافقة' : 'Pending Partners'}: ${stats.pending}

═══════════════════════════════
${isRtl ? 'الحجوزات' : 'Bookings'}
═══════════════════════════════
${isRtl ? 'إجمالي الحجوزات' : 'Total Bookings'}: ${stats.bookings}
${isRtl ? 'حجوزات اليوم' : "Today's Bookings"}: ${stats.todayBookings}
${isRtl ? 'الحجوزات المكتملة' : 'Completed Bookings'}: ${stats.completedBookings}

═══════════════════════════════
${isRtl ? 'الإيرادات' : 'Revenue'}
═══════════════════════════════
${isRtl ? 'إجمالي الإيرادات' : 'Total Revenue'}: ${stats.revenue} ${isRtl ? 'ر.س' : 'SAR'}

═══════════════════════════════
${isRtl ? 'الأكثر حجزاً — المراكز' : 'Top Venues'}
═══════════════════════════════
${topStats.businesses.map((b, i) => `${i+1}. ${b.name}: ${b.count} ${isRtl ? 'حجز' : 'bookings'}`).join('\n') || (isRtl ? 'لا توجد بيانات' : 'No data')}

═══════════════════════════════
${isRtl ? 'الأكثر حجزاً — الخدمات' : 'Top Services'}
═══════════════════════════════
${topStats.services.map((s, i) => `${i+1}. ${s.name}: ${s.count} ${isRtl ? 'حجز' : 'bookings'}`).join('\n') || (isRtl ? 'لا توجد بيانات' : 'No data')}

═══════════════════════════════
${isRtl ? 'الأكثر حجزاً — الخبراء' : 'Top Experts'}
═══════════════════════════════
${topStats.staff.map((s, i) => `${i+1}. ${s.name}: ${s.count} ${isRtl ? 'حجز' : 'bookings'}`).join('\n') || (isRtl ? 'لا توجد بيانات' : 'No data')}

═══════════════════════════════
${isRtl ? 'الرسم البياني' : 'Chart Data'} (${chartPeriod} ${isRtl ? 'يوم' : 'days'})
═══════════════════════════════
${weekChart.map(d => `${isRtl ? d.day : d.dayEn} (${d.date}): ${d.count}`).join('\n')}
    `.trim();

    const blob = new Blob(['\uFEFF' + content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jadwil-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportScreenshot = () => {
    const el = document.getElementById('jadwil-dashboard');
    if (!el) return;
    import('html2canvas').then(({ default: html2canvas }) => {
      html2canvas(el, { scale: 2, backgroundColor: '#F9FAFB' }).then(canvas => {
        const link = document.createElement('a');
        link.download = `jadwil-dashboard-${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL();
        link.click();
      });
    }).catch(() => alert(isRtl ? 'تصدير الصورة غير متاح حالياً' : 'Screenshot export not available'));
  };

  const updateBizStatus = async (id, status) => {
    await supabase.from('businesses').update({ status }).eq('id', id);
    setBusinesses(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    setStats(prev => ({ ...prev, pending: prev.pending - (status !== 'pending' ? 1 : 0) }));
  };

  const toggleCategory = async (id, is_active) => {
    await supabase.from('categories').update({ is_active }).eq('id', id);
    setCategories(prev => prev.map(c => c.id === id ? { ...c, is_active } : c));
  };

  const toggleAd = async (id, is_active) => {
    await supabase.from('ads').update({ is_active }).eq('id', id);
    setAds(prev => prev.map(a => a.id === id ? { ...a, is_active } : a));
  };

  const saveRewards = async () => {
    await supabase.from('rewards_settings').update({ bookings_required: rewards.bookings_required, discount_percentage: rewards.discount_percentage }).eq('id', rewards.id);
  };

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cairo, sans-serif', color: COLORS.primary }}>جارٍ التحميل...</div>;

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', background: COLORS.bg, minHeight: '100vh', fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr' }}>
      <div style={{ background: `linear-gradient(135deg, #1F2937, #111827)`, padding: '16px', color: COLORS.white, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>jadwil {isRtl ? 'لوحة الإدارة' : 'Admin Panel'}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{isRtl ? 'صلاحيات كاملة' : 'Full Access'}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={toggleLang} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 20, padding: '5px 12px', color: '#fff', cursor: 'pointer', fontSize: 12, fontFamily: 'Cairo, sans-serif', fontWeight: 700 }}>
            {lang === 'ar' ? 'EN' : 'ع'}
          </button>
          <button onClick={signOut} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 12px', color: COLORS.white, cursor: 'pointer', fontSize: 12, fontFamily: 'Cairo, sans-serif' }}>
            {isRtl ? 'خروج' : 'Logout'}
          </button>
        </div>
      </div>

      <div style={{ padding: 16, paddingBottom: 80 }}>
        {/* Dashboard */}
        {screen === 'dashboard' && (
          <>
            {/* تنبيه الشركاء المعلقين */}
            {stats.pending > 0 && (
              <div style={{ background: '#FEF2F2', borderRadius: 14, padding: 14, border: `1px solid #FECACA`, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.error }}>⚠️ {stats.pending} {isRtl ? 'طلب بانتظار الموافقة' : 'pending approvals'}</div>
                <button onClick={() => setScreen('businesses')} style={{ fontSize: 12, color: COLORS.error, background: 'none', border: `1px solid ${COLORS.error}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                  {isRtl ? 'مراجعة ←' : 'Review →'}
                </button>
              </div>
            )}

            {/* إحصائيات العملاء والشركاء */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              {[
                { icon: '👤', num: stats.customers, labelAr: 'العملاء', labelEn: 'Customers', color: '#6366F1' },
                { icon: '🏪', num: stats.partners, labelAr: 'الشركاء', labelEn: 'Partners', color: COLORS.primary },
                { icon: '✅', num: stats.activeBusinesses, labelAr: 'شركاء نشطون', labelEn: 'Active Partners', color: COLORS.success },
                { icon: '⏳', num: stats.pending, labelAr: 'انتظار موافقة', labelEn: 'Pending', color: '#F59E0B', alert: stats.pending > 0 },
              ].map((s, i) => (
                <div key={i} style={{ background: s.alert ? '#FEF2F2' : '#fff', borderRadius: 14, padding: 14, border: `1px solid ${s.alert ? '#FECACA' : COLORS.border}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.num}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{isRtl ? s.labelAr : s.labelEn}</div>
                </div>
              ))}
            </div>

            {/* إحصائيات الحجوزات */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { icon: '📅', num: stats.todayBookings, labelAr: 'حجوزات اليوم', labelEn: "Today's", color: '#3B82F6' },
                { icon: '📊', num: stats.bookings, labelAr: 'إجمالي', labelEn: 'Total', color: COLORS.primary },
                { icon: '💰', num: `${stats.revenue || 0}`, labelAr: 'ر.س مكتمل', labelEn: 'SAR earned', color: COLORS.success },
              ].map((s, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 14, padding: 12, border: `1px solid ${COLORS.border}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 18, marginBottom: 3 }}>{s.icon}</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: s.color }}>{s.num}</div>
                  <div style={{ fontSize: 9, color: COLORS.textMuted }}>{isRtl ? s.labelAr : s.labelEn}</div>
                </div>
              ))}
            </div>

            {/* رسم بياني تفاعلي */}
            <div id="jadwil-dashboard" style={{ background: '#fff', borderRadius: 16, padding: 16, marginBottom: 16, border: `1px solid ${COLORS.border}` }}>
              {/* عنوان وأزرار التحكم */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{isRtl ? 'الرسم البياني' : 'Chart'}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {/* نوع البيانات */}
                  {[['bookings', '📅', isRtl ? 'حجوزات' : 'Bookings'], ['revenue', '💰', isRtl ? 'إيرادات' : 'Revenue']].map(([val, icon, label]) => (
                    <button key={val} onClick={() => setChartMetric(val)} style={{ padding: '4px 10px', borderRadius: 8, border: `1.5px solid ${chartMetric === val ? COLORS.primary : COLORS.border}`, background: chartMetric === val ? COLORS.primaryLight : '#fff', color: chartMetric === val ? COLORS.primary : COLORS.textMuted, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'Cairo, sans-serif' }}>
                      {icon} {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* فترة زمنية */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {[[7, isRtl ? '7 أيام' : '7D'], [30, isRtl ? '30 يوم' : '30D'], [90, isRtl ? '3 أشهر' : '3M']].map(([val, label]) => (
                  <button key={val} onClick={() => setChartPeriod(val)} style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: `1.5px solid ${chartPeriod === val ? COLORS.primary : COLORS.border}`, background: chartPeriod === val ? COLORS.primary : '#fff', color: chartPeriod === val ? '#fff' : COLORS.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                    {label}
                  </button>
                ))}
                {/* نوع الرسم */}
                <button onClick={() => setChartType(t => t === 'bar' ? 'line' : 'bar')} style={{ padding: '6px 12px', borderRadius: 8, border: `1.5px solid ${COLORS.border}`, background: '#fff', color: COLORS.textMuted, cursor: 'pointer', fontSize: 14 }}>
                  {chartType === 'bar' ? '📈' : '📊'}
                </button>
              </div>

              {/* الرسم */}
              {weekChart.length > 0 && (() => {
                const maxCount = Math.max(...weekChart.map(w => w.count), 1);
                const showEvery = chartPeriod <= 7 ? 1 : chartPeriod <= 30 ? 3 : 7;
                const points = weekChart.map((d, i) => ({
                  x: (i / (weekChart.length - 1)) * 100,
                  y: 100 - (d.count / maxCount) * 80,
                }));

                return (
                  <div>
                    {chartType === 'bar' ? (
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: chartPeriod <= 7 ? 6 : chartPeriod <= 30 ? 3 : 1, height: 100 }}>
                        {weekChart.map((d, i) => {
                          const height = Math.max((d.count / maxCount) * 85, d.count > 0 ? 4 : 2);
                          return (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              {chartPeriod <= 30 && d.count > 0 && <div style={{ fontSize: 8, color: COLORS.primary, fontWeight: 700 }}>{d.count}</div>}
                              <div style={{ width: '100%', height: height, background: d.isToday ? COLORS.primary : d.count > 0 ? COLORS.primaryLight : '#F3F4F6', borderRadius: '3px 3px 0 0', transition: 'height .3s', position: 'relative' }}>
                                {d.count > 0 && chartPeriod <= 7 && (
                                  <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: COLORS.primary, fontWeight: 700, whiteSpace: 'nowrap' }}>{d.count}</div>
                                )}
                              </div>
                              {i % showEvery === 0 && <div style={{ fontSize: 8, color: d.isToday ? COLORS.primary : COLORS.textMuted, fontWeight: d.isToday ? 700 : 400, whiteSpace: 'nowrap' }}>{isRtl ? d.day : d.dayEn}</div>}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ position: 'relative', height: 100 }}>
                        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                          <polyline points={points.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={COLORS.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <polyline points={`0,100 ${points.map(p => `${p.x},${p.y}`).join(' ')} 100,100`} fill={COLORS.primaryLight} opacity="0.5" />
                          {points.map((p, i) => weekChart[i].count > 0 && (
                            <circle key={i} cx={p.x} cy={p.y} r="2" fill={weekChart[i].isToday ? COLORS.primaryDark : COLORS.primary} />
                          ))}
                        </svg>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                          {weekChart.filter((_, i) => i % showEvery === 0).map((d, i) => (
                            <div key={i} style={{ fontSize: 8, color: d.isToday ? COLORS.primary : COLORS.textMuted }}>{isRtl ? d.day : d.dayEn}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* أزرار التصدير */}
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button onClick={exportReport} style={{ flex: 1, padding: '9px 0', background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryBorder}`, borderRadius: 10, color: COLORS.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  📄 {isRtl ? 'تصدير التقرير' : 'Export Report'}
                </button>
                <button onClick={exportScreenshot} style={{ flex: 1, padding: '9px 0', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, color: '#16A34A', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  🖼️ {isRtl ? 'تصدير صورة' : 'Export Image'}
                </button>
              </div>
            </div>

            {/* الأكثر حجزاً */}
            {(topStats.businesses.length > 0 || topStats.services.length > 0 || topStats.staff.length > 0) && (
              <div style={{ background: '#fff', borderRadius: 16, padding: 16, marginBottom: 16, border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>🏆 {isRtl ? 'الأكثر حجزاً' : 'Top Booked'}</div>

                {/* تبويبات */}
                {[
                  { key: 'businesses', labelAr: '🏪 المراكز', labelEn: '🏪 Venues' },
                  { key: 'services', labelAr: '✂️ الخدمات', labelEn: '✂️ Services' },
                  { key: 'staff', labelAr: '👤 الخبراء', labelEn: '👤 Experts' },
                ].map(tab => (
                  topStats[tab.key].length > 0 && (
                    <div key={tab.key} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8 }}>{isRtl ? tab.labelAr : tab.labelEn}</div>
                      {topStats[tab.key].map((item, i) => {
                        const maxCount = topStats[tab.key][0].count;
                        const pct = (item.count / maxCount) * 100;
                        return (
                          <div key={i} style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                              <div style={{ fontSize: 12, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? COLORS.primary : COLORS.text }}>
                                {i === 0 ? '🥇 ' : i === 1 ? '🥈 ' : i === 2 ? '🥉 ' : `${i+1}. `}{item.name}
                              </div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary }}>{item.count} {isRtl ? 'حجز' : 'bookings'}</div>
                            </div>
                            <div style={{ height: 6, background: COLORS.bg, borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, background: i === 0 ? COLORS.primary : COLORS.primaryLight, borderRadius: 3, transition: 'width .5s' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                ))}
              </div>
            )}

            {/* آخر الحجوزات */}
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>{isRtl ? 'آخر الحجوزات في التطبيق' : 'Latest Bookings'}</div>
            {recentBookings.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted, fontSize: 13 }}>{isRtl ? 'لا توجد حجوزات بعد' : 'No bookings yet'}</div>}
            {recentBookings.map(b => (
              <div key={b.id} style={{ background: '#fff', borderRadius: 12, padding: 12, marginBottom: 8, border: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{b.profiles && b.profiles.full_name}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                    {isRtl ? (b.businesses && b.businesses.name_ar) : (b.businesses && (b.businesses.name_en || b.businesses.name_ar))} · {b.booking_date}
                  </div>
                </div>
                <div style={{ textAlign: isRtl ? 'left' : 'right' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary }}>{b.price} {isRtl ? 'ر.س' : 'SAR'}</div>
                  <div style={{ fontSize: 10, color: b.status === 'completed' ? COLORS.success : b.status === 'pending' ? '#F59E0B' : COLORS.textMuted }}>
                    {b.status === 'pending' ? (isRtl ? 'انتظار' : 'Pending') : b.status === 'confirmed' ? (isRtl ? 'مؤكد' : 'Confirmed') : b.status === 'completed' ? (isRtl ? 'مكتمل' : 'Completed') : (isRtl ? 'ملغي' : 'Cancelled')}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* التجار */}
        {screen === 'businesses' && (
          <>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 14 }}>{isRtl ? 'إدارة التجار' : 'Manage Businesses'}</div>
            {businesses.map(b => (
              <div key={b.id} style={{ background: COLORS.white, borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${b.status === 'pending' ? '#FCD34D' : COLORS.border}` }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {b.logo_url ? <img src={b.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🏪'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{isRtl ? b.name_ar : (b.name_en || b.name_ar)}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>{b.city} · {b.type}</div>
                  </div>
                  <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 8, fontWeight: 700, background: b.status === 'active' ? '#F0FDF4' : b.status === 'pending' ? '#FFFBEB' : '#FEF2F2', color: b.status === 'active' ? COLORS.success : b.status === 'pending' ? '#D97706' : COLORS.error }}>
                    {b.status === 'active' ? (isRtl ? 'نشط' : 'Active') : b.status === 'pending' ? (isRtl ? 'انتظار' : 'Pending') : (isRtl ? 'موقوف' : 'Suspended')}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {b.status === 'pending' && <button onClick={() => updateBizStatus(b.id, 'active')} style={{ flex: 1, padding: '7px 0', background: COLORS.success, border: 'none', borderRadius: 8, color: COLORS.white, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>✓ {isRtl ? 'قبول' : 'Approve'}</button>}
                  {b.status === 'active' && <button onClick={() => updateBizStatus(b.id, 'suspended')} style={{ flex: 1, padding: '7px 0', background: COLORS.error, border: 'none', borderRadius: 8, color: COLORS.white, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'إيقاف' : 'Suspend'}</button>}
                  {b.status === 'suspended' && <button onClick={() => updateBizStatus(b.id, 'active')} style={{ flex: 1, padding: '7px 0', background: COLORS.success, border: 'none', borderRadius: 8, color: COLORS.white, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'تفعيل' : 'Activate'}</button>}
                  {b.status === 'pending' && <button onClick={() => updateBizStatus(b.id, 'suspended')} style={{ flex: 1, padding: '7px 0', background: '#F3F4F6', border: 'none', borderRadius: 8, color: COLORS.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>✕ {isRtl ? 'رفض' : 'Reject'}</button>}
                </div>
              </div>
            ))}
          </>
        )}

        {/* المجالات */}
        {screen === 'categories' && (
          <>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>{isRtl ? 'إدارة المجالات' : 'Manage Categories'}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 14 }}>{isRtl ? 'فعّل المجالات التي تريد إظهارها في التطبيق' : 'Enable categories to show in the app'}</div>
            {categories.map(cat => (
              <div key={cat.id} style={{ background: COLORS.white, borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: cat.is_active ? 1 : 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 24 }}>{cat.icon}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{isRtl ? cat.name_ar : cat.name_en}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted }}>{isRtl ? cat.name_en : cat.name_ar}</div>
                  </div>
                </div>
                <button onClick={() => toggleCategory(cat.id, !cat.is_active)} style={{ padding: '7px 14px', borderRadius: 10, border: 'none', background: cat.is_active ? '#FEF2F2' : '#F0FDF4', color: cat.is_active ? COLORS.error : COLORS.success, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  {cat.is_active ? (isRtl ? '🔴 إيقاف' : '🔴 Hide') : (isRtl ? '🟢 تفعيل' : '🟢 Show')}
                </button>
              </div>
            ))}
          </>
        )}

        {/* الإعلانات */}
        {screen === 'ads' && <AdsManager ads={ads} setAds={setAds} toggleAd={toggleAd} isRtl={isRtl} />}

        {/* المكافآت */}
        {screen === 'rewards' && rewards && (
          <>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 16 }}>{isRtl ? 'إعدادات نظام المكافآت' : 'Rewards Settings'}</div>
            <div style={{ background: COLORS.white, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}` }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>{isRtl ? 'عدد الحجوزات المطلوبة للخصم' : 'Bookings required for discount'}</label>
                <input type="number" value={rewards.bookings_required} onChange={e => setRewards({ ...rewards, bookings_required: Number(e.target.value) })} style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>{isRtl ? 'نسبة الخصم (%)' : 'Discount percentage (%)'}</label>
                <input type="number" value={rewards.discount_percentage} onChange={e => setRewards({ ...rewards, discount_percentage: Number(e.target.value) })} style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ background: COLORS.primaryLight, borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13, color: COLORS.primaryDark }}>
                🎁 {isRtl ? `بعد كل ${rewards.bookings_required} حجوزات يحصل المستخدم على خصم ${rewards.discount_percentage}%` : `After every ${rewards.bookings_required} bookings, user gets ${rewards.discount_percentage}% discount`}
              </div>
              <button onClick={saveRewards} style={{ width: '100%', padding: 13, background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, border: 'none', borderRadius: 12, color: COLORS.white, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                {isRtl ? 'حفظ الإعدادات ✓' : 'Save Settings ✓'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* شريط التنقل */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1F2937', display: 'flex', padding: '8px 0 14px', zIndex: 100, boxShadow: '0 -4px 20px rgba(0,0,0,0.2)' }}>
        {NAV.map(item => (
          <button key={item.id} onClick={() => setScreen(item.id)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '4px 0', fontFamily: 'Cairo, sans-serif' }}>
            <span style={{ fontSize: 22 }}>{item.icon}</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: screen === item.id ? COLORS.primary : '#9CA3AF' }}>
              {isRtl ? item.labelAr : item.labelEn}
            </span>
            {screen === item.id && <div style={{ width: 4, height: 4, borderRadius: '50%', background: COLORS.primary }} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function AdsManager({ ads, setAds, toggleAd, isRtl }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title_ar: '', title_en: '', media_url: '', media_type: 'image', link_url: '' });

  const addAd = async () => {
    if (!form.media_url) return;
    const { data } = await supabase.from('ads').insert({ ...form, is_active: true, sort_order: ads.length }).select().single();
    if (data) { setAds(prev => [...prev, data]); setShowAdd(false); setForm({ title_ar: '', title_en: '', media_url: '', media_type: 'image', link_url: '' }); }
  };

  const INPUT = { width: '100%', padding: '10px 12px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', marginBottom: 10, direction: isRtl ? 'rtl' : 'ltr' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text }}>{isRtl ? 'الإعلانات' : 'Ads'}</div>
        <button onClick={() => setShowAdd(!showAdd)} style={{ background: COLORS.primary, border: 'none', borderRadius: 10, padding: '8px 14px', color: COLORS.white, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
          {isRtl ? '+ إضافة' : '+ Add'}
        </button>
      </div>
      {showAdd && (
        <div style={{ background: COLORS.white, borderRadius: 14, padding: 16, marginBottom: 14, border: `1px solid ${COLORS.primaryBorder}` }}>
          <input style={INPUT} placeholder={isRtl ? 'عنوان الإعلان بالعربي' : 'Ad title in Arabic'} value={form.title_ar} onChange={e => setForm({ ...form, title_ar: e.target.value })} />
          <input style={INPUT} placeholder={isRtl ? 'رابط الصورة/الفيديو *' : 'Image/Video URL *'} value={form.media_url} onChange={e => setForm({ ...form, media_url: e.target.value })} />
          <select style={{ ...INPUT, cursor: 'pointer' }} value={form.media_type} onChange={e => setForm({ ...form, media_type: e.target.value })}>
            <option value="image">{isRtl ? 'صورة' : 'Image'}</option>
            <option value="gif">GIF</option>
            <option value="video">{isRtl ? 'فيديو' : 'Video'}</option>
          </select>
          <input style={INPUT} placeholder={isRtl ? 'رابط عند الضغط (اختياري)' : 'Click URL (optional)'} value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={addAd} style={{ flex: 1, padding: '10px 0', background: COLORS.primary, border: 'none', borderRadius: 10, color: COLORS.white, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'إضافة' : 'Add'}</button>
            <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px 0', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, cursor: 'pointer', fontSize: 13, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'إلغاء' : 'Cancel'}</button>
          </div>
        </div>
      )}
      {ads.map(ad => (
        <div key={ad.id} style={{ background: COLORS.white, borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${COLORS.border}`, opacity: ad.is_active ? 1 : 0.5 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
            {ad.media_url && <img src={ad.media_url} alt="" style={{ width: 60, height: 40, borderRadius: 8, objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{ad.title_ar || (isRtl ? 'بدون عنوان' : 'No title')}</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted }}>{ad.media_type}</div>
            </div>
          </div>
          <button onClick={() => toggleAd(ad.id, !ad.is_active)} style={{ width: '100%', padding: '7px 0', borderRadius: 8, border: 'none', background: ad.is_active ? '#FEF2F2' : '#F0FDF4', color: ad.is_active ? COLORS.error : COLORS.success, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
            {ad.is_active ? (isRtl ? '🔴 إيقاف' : '🔴 Disable') : (isRtl ? '🟢 تفعيل' : '🟢 Enable')}
          </button>
        </div>
      ))}
    </>
  );
}
